import { register } from "async-ops";
import {
  AuditListItemResponse,
  AuditDetailsResponse,
  AuditKpiResponse,
  AuditUrgencyItemResponse,
  TypeaheadResponse,
  AuditListParams,
  AuditKpiParams,
  AuditUrgencyParams,
  AuditTypeaheadParams,
  PagedResponse,
  PagedResult,
  PaginationInfo,
} from "../types";
import {
  buildListQuery,
  buildKpiQuery,
  buildUrgencyQuery,
  buildTypeaheadQuery,
} from "./query-builder";
import { callEnterpriseApi } from "./enterprise-api";
import { AUDIT_ROUTES } from "./api-routes";

const get = callEnterpriseApi;

// Async-ops operation keys for the Audit Queue page.
export const GET_INTERNAL_AUDIT_LIST = "GET_INTERNAL_AUDIT_LIST";
export const GET_AUDIT_DETAILS = "GET_AUDIT_DETAILS";
export const GET_AUDIT_KPIS = "GET_AUDIT_KPIS";
export const GET_AUDIT_URGENCY = "GET_AUDIT_URGENCY";
export const GET_AUDIT_TYPES = "GET_AUDIT_TYPES";
export const GET_AUDIT_AUDITORS = "GET_AUDIT_AUDITORS";
export const GET_AUDIT_PERIODS = "GET_AUDIT_PERIODS";

// Normalizes the v1 PagedResponse envelope. The wire only carries totalItems,
// so hasNext is derived (page * pageSize < totalItems).
export const toPagedResult = (
  response: PagedResponse<AuditListItemResponse> | null,
  params: AuditListParams
): PagedResult<AuditListItemResponse> => {
  const page = response?.pagination?.page ?? params.page;
  const pageSize = response?.pagination?.pageSize ?? params.pageSize;
  const totalCount = response?.pagination?.totalItems ?? 0;

  return {
    items: response?.data ?? [],
    totalCount,
    page,
    pageSize,
    hasNext: page * pageSize < totalCount,
    facets: response?.facets ?? [],
    facetsRequested: params.includeFacets === true,
  };
};


// Add labels to facets from API response
const addFacetLabels = (facets: any[]): any[] => {
  const FACET_LABELS: Record<string, string> = {
    'Status': 'Status',
    'AuditGroup': 'Audit Group',
    'Outcome': 'Outcome',
    'QAReviewer': 'QA Reviewer',
  };

  return (facets || []).map((facet: any) => ({
    ...facet,
    label: FACET_LABELS[facet.field] || facet.field,
  }));
};

// 1. Paginated audit list (All Audits / My Audits with filters, search, sort)
const auditListService = async (
  params: AuditListParams
): Promise<PagedResult<AuditListItemResponse>> => {
  try {
    const queryString = buildListQuery(params);
    const url = `${AUDIT_ROUTES.list}?${queryString}`;

    const data = (await get(url)) as PagedResponse<AuditListItemResponse>;
    const items = data?.data || [];
    const pagination: Partial<PaginationInfo> = data?.pagination || {};
    const facets = data?.facets || [];

    // Add labels to facets from API response
    const facetsWithLabels = addFacetLabels(facets);

    return {
      items: items,
      totalCount: pagination.totalItems || 0,
      page: pagination.page || params.page || 1,
      pageSize: pagination.pageSize || params.pageSize || 25,
      hasNext: (pagination.page || 1) * (pagination.pageSize || 25) < (pagination.totalItems || 0),
      facets: facetsWithLabels,
      facetsRequested: params.includeFacets === true,
    };
  } catch (error: any) {
    console.error('[auditListService] Error:', error?.message || error);
    return {
      items: [],
      totalCount: 0,
      page: params.page || 1,
      pageSize: params.pageSize || 25,
      hasNext: false,
      facets: [],
      // A failed call carries no facet block either way, so the dropdowns keep
      // whatever they had rather than emptying on a transient error.
      facetsRequested: false,
    };
  }
};

// 2. Single audit detail (22 fields across 4 panels)
const auditDetailsService = async (auditId: number): Promise<AuditDetailsResponse | null> => {
  const response = (await get(AUDIT_ROUTES.detail(auditId))) as AuditDetailsResponse;
  return response ?? null;
};

// 3. Snapshot KPI cards (header metrics)
const auditKpisService = async (params: AuditKpiParams): Promise<AuditKpiResponse | null> => {
  const response = (await get(
    `${AUDIT_ROUTES.kpis}?${buildKpiQuery(params)}`
  )) as AuditKpiResponse;
  return response ?? null;
};

// 4. Urgency list (configurable "N Audits Require Immediate Action")
const auditUrgencyService = async (
  params: AuditUrgencyParams
): Promise<AuditUrgencyItemResponse[]> => {
  const response = (await get(
    `${AUDIT_ROUTES.urgent}?${buildUrgencyQuery(params)}`
  )) as AuditUrgencyItemResponse[];
  return response ?? [];
};

// Helper: Build urgency query with criteria ranks
export const buildUrgencyCriteriaQuery = (criteria: any): string => {
  const params = new URLSearchParams();
  // Add criteria ranks as query parameters
  if (criteria.overdueRank !== undefined) params.append('overdueRank', criteria.overdueRank.toString());
  if (criteria.dueSoonRank !== undefined) params.append('dueSoonRank', criteria.dueSoonRank.toString());
  if (criteria.stalledRank !== undefined) params.append('stalledRank', criteria.stalledRank.toString());
  if (criteria.stalledDays !== undefined) params.append('stalledDays', criteria.stalledDays.toString());
  if (criteria.topN !== undefined) params.append('topN', criteria.topN.toString());
  return params.toString();
};

// 5. Typeahead filter: audit types
const auditTypesService = async (
  params: AuditTypeaheadParams
): Promise<TypeaheadResponse[]> => {
  // Backend now supports 0 characters for typeahead
  const response = (await get(
    `${AUDIT_ROUTES.auditTypeOptions}?${buildTypeaheadQuery(params)}`
  )) as TypeaheadResponse[];
  return response ?? [];
};

// 6. Typeahead filter: auditors
const auditAuditorsService = async (
  params: AuditTypeaheadParams
): Promise<TypeaheadResponse[]> => {
  // Backend now supports 0 characters for typeahead
  const response = (await get(
    `${AUDIT_ROUTES.auditorOptions}?${buildTypeaheadQuery(params)}`
  )) as TypeaheadResponse[];
  return response ?? [];
};

// 7. Typeahead filter: audit periods
const auditPeriodsService = async (
  params: AuditTypeaheadParams
): Promise<TypeaheadResponse[]> => {
  // Backend now supports 0 characters for typeahead
  const response = (await get(
    `${AUDIT_ROUTES.auditPeriodOptions}?${buildTypeaheadQuery(params)}`
  )) as TypeaheadResponse[];
  return response ?? [];
};


// Mocks for async-ops mock mode
const mockPagedResult = (): PagedResult<AuditListItemResponse> => ({
  items: [
    { auditId: 1001, auditTitle: "Medicare Provider A Q1 2024", auditPathwayId: null, auditPathwayName: "-", auditGroupId: 1, auditGroupName: "Medicare", auditPeriodName: "Q1 2024", auditPeriodEndDate: "2024-03-31", isOverdue: true, isDueThisWeek: false, primaryAuditorUserId: 101, primaryAuditorName: "John Smith", auditStatus: "Open", statusType: "Open", updateDate: "2024-07-10", auditOutcomeId: null, auditOutcome: null, outcomeType: null,  auditTypeId: null, auditType: null, lastComment: null, lastCommentDate: null, organizeAuditInd: false, organizeAuditByFieldName: null, totalCaseCount: 10, completedCaseCount: 8, entityCount: null, completedEntityCount: null, score: "85" },
    { auditId: 1002, auditTitle: "Medicaid Facility B Q1 2024", auditPathwayId: null, auditPathwayName: "-", auditGroupId: 2, auditGroupName: "Medicaid", auditPeriodName: "Q1 2024", auditPeriodEndDate: "2024-04-15", isOverdue: false, isDueThisWeek: true, primaryAuditorUserId: 102, primaryAuditorName: "Sarah Johnson", auditStatus: "In Progress", statusType: "InProgress", updateDate: "2024-07-12", auditOutcomeId: null, auditOutcome: null,  outcomeType: null, auditTypeId: null, auditType: null, lastComment: null, lastCommentDate: null, organizeAuditInd: false, organizeAuditByFieldName: null, totalCaseCount: 15, completedCaseCount: 12, entityCount: null, completedEntityCount: null, score: "92" },
    { auditId: 1003, auditTitle: "Commercial Plan C Q2 2024", auditPathwayId: null, auditPathwayName: "-", auditGroupId: 3, auditGroupName: "Commercial", auditPeriodName: "Q2 2024", auditPeriodEndDate: "2024-06-30", isOverdue: false, isDueThisWeek: true, primaryAuditorUserId: 103, primaryAuditorName: "Mike Davis", auditStatus: "Open", statusType: "Open", updateDate: "2024-07-11", auditOutcomeId: null, auditOutcome: null, auditTypeId: null,  outcomeType: null, auditType: null, lastComment: null, lastCommentDate: null, organizeAuditInd: false, organizeAuditByFieldName: null, totalCaseCount: 8, completedCaseCount: 5, entityCount: null, completedEntityCount: null, score: "78" },
    { auditId: 1004, auditTitle: "Provider D Annual Review", auditPathwayId: null, auditPathwayName: "-", auditGroupId: 1, auditGroupName: "Medicare", auditPeriodName: "2024", auditPeriodEndDate: "2024-07-14", isOverdue: false, isDueThisWeek: false, primaryAuditorUserId: 104, primaryAuditorName: "Lisa Chen", auditStatus: "Closed", statusType: "Closed", updateDate: "2024-07-14", auditOutcomeId: 1, auditOutcome: "Compliant", auditTypeId: null,  outcomeType: null, auditType: null, lastComment: null, lastCommentDate: null, organizeAuditInd: false, organizeAuditByFieldName: null, totalCaseCount: 20, completedCaseCount: 20, entityCount: null, completedEntityCount: null, score: "100" },
    { auditId: 1005, auditTitle: "Facility E Q3 2024", auditPathwayId: null, auditPathwayName: "-", auditGroupId: 2, auditGroupName: "Medicaid", auditPeriodName: "Q3 2024", auditPeriodEndDate: "2024-09-30", isOverdue: false, isDueThisWeek: false, primaryAuditorUserId: 101, primaryAuditorName: "John Smith", auditStatus: "Open", statusType: "Open", updateDate: "2024-07-13", auditOutcomeId: null, auditOutcome: null, auditTypeId: null, auditType: null,  outcomeType: null, lastComment: null, lastCommentDate: null, organizeAuditInd: false, organizeAuditByFieldName: null, totalCaseCount: 6, completedCaseCount: 3, entityCount: null, completedEntityCount: null, score: "65" },
  ],
  totalCount: 5,
  page: 1,
  pageSize: 25,
  hasNext: false,
  facets: [],
  facetsRequested: false,
});

const mockKpisResult = (): AuditKpiResponse | null => ({
  totalOpen: 4,
  overdue: 1,
  dueThisWeek: 2,
  completed30D: 3,
  totalImpact30D: 150000,
});

const mockUrgencyResult = (): AuditUrgencyItemResponse[] => [
  { auditId: 1001, auditTitle: "Medicare Provider A Q1 2024", auditPathwayName: "-", primaryAuditorName: "John Smith", primaryAuditorUserId: 101, auditStatus: "Open", statusType: "New", auditPeriodEndDate: "2024-03-31", daysOverdue: 15, matchedRank: 1, matchedCriterion: "Overdue Audits", totalCaseCount: 10, completedCaseCount: 8 },
  { auditId: 1003, auditTitle: "Commercial Plan C Q2 2024", auditPathwayName: "-", primaryAuditorName: "Mike Davis", primaryAuditorUserId: 103, auditStatus: "Open", statusType: "New", auditPeriodEndDate: "2024-06-30", daysOverdue: -5, matchedRank: 2, matchedCriterion: "Due Soon", totalCaseCount: 8, completedCaseCount: 5 },
];

// Register all services with async-ops
register(GET_INTERNAL_AUDIT_LIST, auditListService, () => Promise.resolve(mockPagedResult()));
register(GET_AUDIT_DETAILS, auditDetailsService, () => Promise.resolve(null));
register(GET_AUDIT_KPIS, auditKpisService, () => Promise.resolve(mockKpisResult()));
register(GET_AUDIT_URGENCY, auditUrgencyService, () => Promise.resolve(mockUrgencyResult()));
register(GET_AUDIT_TYPES, auditTypesService, () => Promise.resolve([]));
register(GET_AUDIT_AUDITORS, auditAuditorsService, () => Promise.resolve([]));
register(GET_AUDIT_PERIODS, auditPeriodsService, () => Promise.resolve([]));
